import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUser, useEnvironment, useGlobal, useSocket } from "./core/Global";
import { DesktopNotificationItem } from "./models/Notification";
import InAppNotifications, { InAppNotificationItem } from "./components/notifications/InAppNotifications";
import { NotificationTypeSettings, normalizeNotificationsSettings } from "./modules/system/settings/models/NotificationsSettings";
import { playChatNotificationSound } from "./utilities/notificationSound";


interface IProps {
    userId: string
    ownerId: string
    isTech?: boolean
}

const MAX_INAPP_ITEMS = 3

// Temporarily off on phones: the native app raises its own FCM notification while it
// is in the foreground, so the banner doubled up there. Flip back to true once the
// native side stops showing a notification while the app is resumed — the whole
// in-app banner path stays in place and works as soon as this is true again.
const INAPP_ON_MOBILE = false

// Same threshold useScreen() uses for isMobile. Read when a notification arrives
// rather than captured, since the socket handler is registered only once.
const MOBILE_MAX_WIDTH = 600


export default function NotificationCenter(props: IProps) {

    const socket = useSocket()
    const env = useEnvironment()
    const navigate = useNavigate()
    // Not for reading the user — for re-rendering when the global state (and with it
    // the user's saved settings) changes, so the ref below is refreshed.
    const [globalState] = useGlobal()

    const [inAppItems, inAppItemsSet] = useState<InAppNotificationItem[]>([])

    // The socket handler below is registered once, so the settings it reads have to
    // come from a ref — a captured value would freeze at whatever was loaded first.
    const settings = useRef(normalizeNotificationsSettings(getUser()?.notificationsSettings))

    useEffect(() => {
        settings.current = normalizeNotificationsSettings(getUser()?.notificationsSettings)
    }, [globalState.user])

    useEffect(() => {

        var socketKey = socket.start(
            skt => {
                // No owner id for a technician: that group carries every chat of the
                // account, and they only ever hear about their own.
                skt.invoke("StartListenToDesktopNotifs", props.userId, props.isTech ? "" : props.ownerId)
                    .then(() => {

                        skt.on("PushDesktopNotification", (notif: DesktopNotificationItem) => {

                            if (env.isDevelopment) console.log("new desktop notif recieved!", document.visibilityState, notif);

                            // The owner group broadcast reaches every member, so skip the sender explicitly
                            // (used by internal/system chats where the sender is also an office user).
                            if (notif.excludeUserId && notif.excludeUserId === props.userId) return;

                            if (notif.objectType == "chat") {
                                chatNotification(notif)
                                return
                            }

                            if (document.visibilityState == "visible") return;
                            systemNotification(notif)
                        })
                    });

            }, skt => {
                skt.invoke("StopListenToDesktopNotifs", props.userId, props.isTech ? "" : props.ownerId);
                skt.off("PushDesktopNotification");
            });

        return () => {
            socket.stop(socketKey);
        }

    }, [])


    // Chat notifications follow this user's own settings — the server broadcasts to
    // everyone and each client decides for its user. The in-app banner and its sound
    // fire whatever the app is doing (waiting on screen for when the user comes back),
    // while the device notification is only raised when the app is not in front of
    // them, since that is the case it exists for.
    const chatNotification = (notif: DesktopNotificationItem) => {

        const chat: NotificationTypeSettings = settings.current.chat
        if (!chat.enabled) return

        // Focus is deliberately not part of this: a window the user can see next to
        // the one they are typing in is still in front of them. Chrome/Edge already
        // report a minimized or fully covered window as hidden.
        const hidden = document.visibilityState != "visible"

        // No banner when the message lands in the conversation the user is looking at
        // right now, and none at all on a phone while INAPP_ON_MOBILE is off.
        const reading = !hidden && window.location.pathname.startsWith("/chat/" + notif.objectId)
        const banner = INAPP_ON_MOBILE || window.innerWidth >= MOBILE_MAX_WIDTH

        var sounded = false

        if (banner && chat.inApp && !reading) {

            inAppItemsSet(prev => [
                ...prev.filter(f => f.id != notif.tag),
                {
                    id: notif.tag,
                    title: notif.title,
                    body: notif.body,
                    icon: notif.icon,
                    link: "/chat/" + notif.objectId,
                    keepOnScreen: chat.keepOnScreen
                }
            ].slice(-MAX_INAPP_ITEMS))

            if (chat.playSound) {
                playChatNotificationSound()
                sounded = true
            }
        }

        // Not for technicians: their device notification comes from the web push the
        // chat send endpoints fire, and raising a second one here would double it up.
        if (!props.isTech && hidden && chat.push) systemNotification(notif, sounded)
    }


    const systemNotification = (notif: DesktopNotificationItem, muted?: boolean) => {

        if (!("Notification" in window)) return

        if (Notification.permission === "granted") {
            push(notif, muted)
        }
        else if (Notification.permission !== "denied") {
            Notification.requestPermission((permission) => {
                if (permission === "granted") push(notif, muted)
            })
        }
    }


    const push = (notif: DesktopNotificationItem, muted?: boolean) => {

        const n = new Notification(notif.title, {
            body: notif.body,
            tag: notif.tag,
            icon: notif.icon,
            // Sound and staying on screen belong to the in-app banner, so the device
            // notification is left to the device. The only exception: when the banner
            // already played the chat sound, this one keeps quiet instead of doubling it.
            silent: muted ? true : undefined
        });

        n.onclick = () => {
            window.focus();

            if (notif.objectType == "chat") {
                var link = "/chat/" + notif.objectId;
                if (!window.location.pathname.startsWith(link)) {
                    document.location.href = link;
                }
            }

        }
    }


    return (
        <InAppNotifications items={inAppItems}
            onOpen={item => {
                // Opening one takes the user away from wherever they were, so the
                // whole stack has served its purpose.
                inAppItemsSet([])
                if (item.link) navigate(item.link)
            }}
            onClose={id => inAppItemsSet(prev => prev.filter(f => f.id != id))} />
    )
}
